/**
 * ponBlob — the DOM/SVG Pon Pon character (the plaza Pon). HTML string builder
 * ported from the prototype `docs/design/flow/ui.jsx` `PonBlob` (elementless
 * branch — Section 1 has no element FX). Built from the SAME `ponArt` atoms the
 * canvas `PonCharacter` rasterises, so the DOM Pon and the in-stage canvas Pon
 * are the identical gumdrop. Animation states are driven by swapping the
 * `.pon-fig` class via `CSS_CLASS_FOR` (`./ponState.ts`).
 */

import type { CostumeId } from '@ponponland/shared';
import { ponBodyInner, type Eyes } from './ponArt.js';

export interface PonBlobOptions {
  colour: string;
  costume: CostumeId;
  size?: number;
  eyes?: Eyes;
  /** Add the idle breathing animation (Splash hero). */
  idle?: boolean;
  /**
   * Shift the face (eyes/blush/mouth) this many viewBox px toward the right so
   * the Pon reads as facing a direction in the 2D world (the host mirrors the
   * whole sprite with `scaleX(-1)` to face left). 0 = front-facing (menus).
   */
  faceShift?: number;
}

/**
 * An elementless Pon Pon blob as an inline SVG-in-div HTML string: gumdrop body
 * with a radial-gradient fill, gloss highlights, blush, eyes, mouth, costume.
 */
export function ponBlobHtml({ colour, costume: cos, size = 92, eyes = 'happy', idle = false, faceShift = 0 }: PonBlobOptions): string {
  // `pon-fig` is a stable hook so a host (e.g. the lobby's interactive Pon) can
  // swap the animation state class (idle/walk) per frame from the local sim.
  const animClass = idle ? 'pon-fig pon-idle' : 'pon-fig';
  // `absorbMouth:'css'` keeps the absorb mouth controlled by the stylesheet.
  const inner = ponBodyInner({ colour, costume: cos, eyes, faceShift, absorbMouth: 'css' });

  return `<div style="position:relative;width:${size}px;height:${size * 1.02}px;display:flex;flex-direction:column;align-items:center">
    <div class="${animClass}">
      <svg class="sprite" width="${size}" height="${size}" viewBox="0 0 100 100" style="overflow:visible">
        ${inner}
      </svg>
    </div>
  </div>`;
}
